/**
 * Support mode on the client (TZ §2.3): which support session this browser acts under,
 * the banner's wording, and when the suspended screen replaces the agency's pages.
 *
 * The session id is kept per browser tab in sessionStorage. It is a pointer, not a
 * credential: the server re-checks the admin, the expiry and the tenant on every request.
 * Storage can be blocked, so every access is guarded and a failure means "no session".
 */

const SESSION_KEY = "kapitan.support-session";

/** Duration choices the platform card offers, in minutes. §2.3's default is the first. */
export const SUPPORT_DURATIONS: readonly { minutes: number; label: string }[] = [
  { minutes: 30, label: "30 daqiqa" },
  { minutes: 60, label: "60 daqiqa" },
  { minutes: 120, label: "2 soat" },
];

export function readSupportSessionId(): number | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (raw === null) return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function writeSupportSessionId(id: number): void {
  try {
    window.sessionStorage.setItem(SESSION_KEY, String(id));
  } catch {
    // Storage blocked: the session still exists on the server, this tab simply cannot
    // act under it. The platform card says so when the user tries to enter.
  }
}

export function clearSupportSessionId(): void {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** The header value a request carries, or null. Platform-panel calls never carry it. */
export function supportHeaderFor(path: string): string | null {
  if (path.startsWith("/api/platform/")) return null;
  const id = readSupportSessionId();
  return id === null ? null : String(id);
}

/** The banner's sentence, with the reason the platform recorded. */
export function supportBannerText(reason: string): string {
  return `Kapitan qo'llab-quvvatlash xizmati hozir hisobingizda — ${reason}`;
}

/**
 * Whether the suspended screen replaces the page for this user. Owners keep their billing
 * page, so they can see what the suspension costs and what to pay; everyone else, and every
 * other page, gets the screen.
 */
export function showsSuspendedScreen(
  tenantStatus: string | undefined,
  role: string | undefined,
  pathname: string,
): boolean {
  if (tenantStatus !== "suspended_admin") return false;
  const onBilling = pathname.startsWith("/sozlamalar/tarif");
  return !(role === "owner" && onBilling);
}
